import React, { useMemo } from "react";
import { preprocessPieChartData } from "@shared/utils/charts";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import PieChart from "common/Charts/PieChart/PieChart";
import styled, { DefaultTheme, useTheme } from "styled-components";
import { LanguageNameSpaces, MarginTypes } from "@shared/types";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface MarginTypeChartProps {
  data?: { [key in MarginTypes]: number };
}

/** New look: each margin type keeps one colour (cross first). */
const getMarginTypeColor = (theme: DefaultTheme, marginType: MarginTypes) =>
  marginType === MarginTypes.CROSS
    ? theme.colors.primaryBlue
    : theme.colors.pieChartColors[1];

/** New look: cross then isolated, as whole-number shares of all trades. */
const useMarginTypeShares = (data?: { [key in MarginTypes]: number }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  return useMemo(() => {
    const cross = data?.[MarginTypes.CROSS] || 0;
    const isolated = data?.[MarginTypes.ISOLATED] || 0;
    const total = cross + isolated;
    const share = (value: number) =>
      total ? Math.round((value / total) * 10000) / 100 : 0;
    return [
      {
        key: MarginTypes.CROSS,
        name: t("margin_types.cross"),
        value: share(cross),
        color: getMarginTypeColor(theme, MarginTypes.CROSS),
      },
      {
        key: MarginTypes.ISOLATED,
        name: t("margin_types.isolated"),
        value: share(isolated),
        color: getMarginTypeColor(theme, MarginTypes.ISOLATED),
      },
    ];
  }, [data, t, theme]);
};

const GAUGE_RADIUS = 34;
const GAUGE_STROKE = 7;

/** New look: the margin types as a small half ring (the leverage card's aside). */
export const MarginTypeGauge = ({ data }: MarginTypeChartProps) => {
  const shares = useMarginTypeShares(data);
  const width = GAUGE_RADIUS * 2 + GAUGE_STROKE;
  const cx = width / 2;
  const cy = GAUGE_RADIUS + GAUGE_STROKE / 2;
  const height = cy + GAUGE_STROKE / 2;
  const d = `M ${
    cx - GAUGE_RADIUS
  } ${cy} A ${GAUGE_RADIUS} ${GAUGE_RADIUS} 0 0 1 ${cx + GAUGE_RADIUS} ${cy}`;
  const both = shares.every((item) => item.value > 0);
  // A hairline gap between the two parts when both are present.
  const gap = both ? 1.5 : 0;
  let offset = 0;
  return (
    <GaugeSvg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden={true}
    >
      <path className="track" d={d} fill="none" strokeWidth={GAUGE_STROKE} />
      {shares.map((item) => {
        const start = offset;
        offset += item.value;
        if (!item.value) return null;
        return (
          <path
            key={item.key}
            d={d}
            fill="none"
            stroke={item.color}
            strokeWidth={GAUGE_STROKE}
            pathLength={100}
            strokeDasharray={`${Math.max(0, item.value - gap)} 100`}
            strokeDashoffset={-start}
          />
        );
      })}
    </GaugeSvg>
  );
};

const MarginTypeChart = ({ data }: MarginTypeChartProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const theme = useTheme();
  const shares = useMarginTypeShares(data);

  const marginTypeToCounterData = useMemo(() => {
    const MARGIN_TYPE_TO_DISPLAY_NAME = {
      [MarginTypes.ISOLATED]: t("margin_types.isolated"),
      [MarginTypes.CROSS]: t("margin_types.cross"),
    };
    let dataToUse = {
      [MarginTypes.ISOLATED]: data?.[MarginTypes.ISOLATED] || 0,
      [MarginTypes.CROSS]: data?.[MarginTypes.CROSS] || 0,
    };
    return Object.entries(dataToUse).map(([marginType, value]) => ({
      name: MARGIN_TYPE_TO_DISPLAY_NAME[marginType],
      value: value,
    }));
  }, [data, i18n.language]);

  const preprocessPieChart = useMemo(
    () =>
      preprocessPieChartData(
        marginTypeToCounterData,
        theme.colors.pieChartColors
      ),
    [marginTypeToCounterData, theme]
  );

  const pieChartData = useMemo(
    () =>
      preprocessPieChart.map((dataPoint) => ({
        ...dataPoint,
        legendDisplay: (
          <Flex $justifyContent="flex-start">
            <LegendText style={{ marginRight: 4 }}>
              {dataPoint.name}:
            </LegendText>
            <LegendText>{dataPoint.value}%</LegendText>
          </Flex>
        ),
      })),
    [preprocessPieChart]
  );

  const extraInfoStyle = useMemo(() => {
    return { marginLeft: 0, minWidth: isMobile ? 100 : 240 };
  }, [isMobile]);

  const wrapperStyle = useMemo(() => {
    return { height: isMobile ? 54 : 24, justifyContent: "flex-end" };
  }, [isMobile]);

  // New look: an inline legend; the half ring sits on the card's right
  // (MarginTypeGauge).
  if (theme.isNewLook) {
    return (
      <NewLookLegend>
        {shares.map((item) => (
          <span key={item.key}>
            <i style={{ background: item.color }} />
            {item.name}: <strong>{item.value}%</strong>
          </span>
        ))}
      </NewLookLegend>
    );
  }

  return (
    <PieChart
      data={pieChartData}
      halfChart={true}
      horizontal={true}
      chartSize={isMobile ? 110 : 130}
      chartWeight={"83%"}
      wrapperStyle={wrapperStyle}
      chartStyle={CHART_STYLE}
      extraInfoStyle={extraInfoStyle}
      extraInfoOnTheLeft={true}
      numItemsInRow={isMobile ? 1 : 2}
      numRows={isMobile ? 2 : 1}
      gap={CHART_GAP}
      hideTotal={true}
      chartParentElementId="marginTypePieChart"
      tooltipElementId="marginTypeTooltip"
      tooltipMargin={24}
      hideTooltip={isMobile}
      disableHover={isMobile}
    />
  );
};

export default MarginTypeChart;

const CHART_STYLE = { transform: "translateY(2px)" };
const CHART_GAP: [number, number] = [4, 0];

const NewLookLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }
  i {
    width: 6px;
    height: 6px;
    margin-right: 2px;
    border-radius: 50%;
  }
  strong {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
const GaugeSvg = styled.svg`
  display: block;
  .track {
    stroke: ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const LegendText = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
